import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {SERIES_COLORS, formatCurrency, formatCurrencyCompact} from '../utils';

import ChartTooltip from './ChartTooltip';
import {DailyRevenueComparison} from 'store/models/dashboard';
import {FC} from 'react';
import dayjs, {Dayjs} from 'dayjs';
import styles from '../Dashboard.module.scss';
import {useChartColors} from '../useChartColors';

interface Props {
  data?: DailyRevenueComparison;
  startDate?: Dayjs | null;
  endDate?: Dayjs | null;
}

// "23 Jun – 29 Jun 2026" (same year) or "28 Dec 2025 – 03 Jan 2026"
const formatRange = (
  start?: Dayjs | string | null,
  end?: Dayjs | string | null,
): string => {
  if (!start || !end) return '';
  const s = dayjs(start);
  const e = dayjs(end);
  if (!s.isValid() || !e.isValid()) return '';
  const startFmt =
    s.year() === e.year() ? s.format('DD MMM') : s.format('DD MMM YYYY');
  return `${startFmt} – ${e.format('DD MMM YYYY')}`;
};

const RevenueComparisonChart: FC<Props> = ({data, startDate, endDate}) => {
  // current range comes from the selected filter; previous range is returned
  // by the backend (previousStart / previousEnd)
  const chartColors = useChartColors();
  const currentRange = formatRange(startDate, endDate);
  const previousRange = formatRange(data?.previousStart, data?.previousEnd);

  const chartData = (data?.currentPeriod ?? []).map((item, index) => ({
    label: dayjs(item.day).format('DD MMM'),
    current: item.revenue,
    previous: data?.previousPeriod?.[index]?.revenue ?? 0,
  }));

  return (
    <div className={styles.chartCard}>
      <div className={styles.chartHeader}>
        <h3>Revenue Comparison</h3>
        <span className={styles.chartSubtitle}>
          Current vs previous period
        </span>
      </div>

      <div className={styles.comparisonTotals}>
        <div className={styles.totalCurrent}>
          <span>This period</span>
          {currentRange ? (
            <small className={styles.periodRange}>{currentRange}</small>
          ) : null}
          <strong>{formatCurrency(data?.currentTotal)}</strong>
        </div>
        <div className={styles.totalPrevious}>
          <span>Previous period</span>
          {previousRange ? (
            <small className={styles.periodRange}>{previousRange}</small>
          ) : null}
          <strong>{formatCurrency(data?.previousTotal)}</strong>
        </div>
      </div>

      {chartData.length === 0 ? (
        <div className={styles.emptyState}>No data for selected period</div>
      ) : (
        <>
          <div className={styles.chartLegend}>
            <span className={styles.legendItem}>
              <span
                className={styles.legendDot}
                style={{background: SERIES_COLORS.current}}
              />
              This period
            </span>
            <span className={styles.legendItem}>
              <span
                className={styles.legendDot}
                style={{background: SERIES_COLORS.previous}}
              />
              Previous period
            </span>
          </div>

          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={chartData} barGap={6} margin={{top: 8, right: 8}}>
              <defs>
                <linearGradient id="barCurrent" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={SERIES_COLORS.current} />
                  <stop
                    offset="100%"
                    stopColor={SERIES_COLORS.current}
                    stopOpacity={0.55}
                  />
                </linearGradient>
                <linearGradient id="barPrevious" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={SERIES_COLORS.previous} />
                  <stop
                    offset="100%"
                    stopColor={SERIES_COLORS.previous}
                    stopOpacity={0.55}
                  />
                </linearGradient>
              </defs>
              <CartesianGrid
                strokeDasharray="3 3"
                vertical={false}
                stroke={chartColors.grid}
              />
              <XAxis
                dataKey="label"
                tick={{fontSize: 12, fill: chartColors.axisText}}
                tickLine={false}
                axisLine={{stroke: chartColors.axisLine}}
              />
              <YAxis
                tick={{fontSize: 12, fill: chartColors.axisText}}
                tickLine={false}
                axisLine={false}
                width={48}
                tickFormatter={(value) => formatCurrencyCompact(Number(value))}
              />
              <Tooltip
                content={<ChartTooltip />}
                cursor={{fill: chartColors.cursor}}
                wrapperStyle={{outline: 'none'}}
              />
              <Bar
                dataKey="current"
                name="This period"
                fill="url(#barCurrent)"
                radius={[6, 6, 0, 0]}
                maxBarSize={26}
              />
              <Bar
                dataKey="previous"
                name="Previous period"
                fill="url(#barPrevious)"
                radius={[6, 6, 0, 0]}
                maxBarSize={26}
              />
            </BarChart>
          </ResponsiveContainer>
        </>
      )}

      {data && !data.hasPreviousData ? (
        <span className={styles.note}>
          No data available for the previous period.
        </span>
      ) : null}
    </div>
  );
};

export default RevenueComparisonChart;
